import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ControlContainer, UntypedFormControl, UntypedFormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatStepper, MatStep, MatStepLabel, MatStepperPrevious } from '@angular/material/stepper';
import { Guid } from 'guid-typescript';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkloadModel } from 'src/app/models/workload.model';
import { WorkloadsService } from 'src/app/services/workloads.service';
import { StepAddressComponent } from '../address/step-address.component';
import { ClientComponent } from '../client/client.component';
import { WorkTaskComponent } from '../work-task/work-task.component';
import { AddressTypeEnum } from 'src/app/shared/enumerators/address-type.enum';
import { ClientModel } from '../../../models/client.model';
import { ToastService } from 'src/app/services/toast.service';
import { WorkloadCoordinatesModel } from 'src/app/models/workload-coordinates.model';
import { ClientService } from 'src/app/services/client.services';
import { WorkTaskService } from 'src/app/services/work-task.service';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { firstValueFrom } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { parseApiValidationError } from './api-validation-error';

import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';

@Component({
    selector: 'app-workload',
    templateUrl: './workload.component.html',
    styleUrls: ['./workload.component.scss'],
    viewProviders: [
        { provide: ControlContainer, useExisting: FormGroupDirective },
    ],
    imports: [FormsModule, ReactiveFormsModule, MatStepper, MatStep, MatStepLabel, ClientComponent, MatIcon, StepAddressComponent, MatStepperPrevious, WorkTaskComponent, MatButton, PageHeaderComponent]
})
export class WorkloadComponent implements OnInit {
  private readonly workloadSvc = inject(WorkloadsService);
  private readonly router = inject(Router);
  toastr = inject(ToastService);
  private readonly clientService = inject(ClientService);
  activatedRoute = inject(ActivatedRoute);
  private readonly workTaskSrv = inject(WorkTaskService);

  @ViewChild(StepAddressComponent, { static: false })
  stepAddressComponent!: StepAddressComponent;
  @ViewChild(ClientComponent, { static: false })
  stepClientComponent!: ClientComponent;
  @ViewChild(WorkTaskComponent, { static: false })
  stepWorkTaskComponent!: WorkTaskComponent;

  @ViewChild('stepper', { static: false }) stepper!: MatStepper;

  workloadForm = new UntypedFormGroup({
    client: new UntypedFormControl(),
  });

  coordinates: WorkloadCoordinatesModel = {};
  workloadId: string = Guid.createEmpty().toString();
  disableSubmit: boolean = true;
  fromCMR: boolean = false;
  // Address to preselect when the wizard is opened for a client from a task ("Generar tarea" in the calendar).
  private addressIdToSelect?: string;

  ngOnInit() {
    this.getCrmParameters();
    this.getClientIdFromUrl();
  }

  // Buscar Id de cliente cuando se llega desde Historico de cliente o desde "Generar tarea" en el calendario
  async getClientIdFromUrl() {
    try {
      const params = await firstValueFrom(this.activatedRoute.queryParams);
      let clientId: string = params['clientId'];

      if (clientId) {
        this.addressIdToSelect = params['addressId'];
        await this.getClient(clientId);
        let queryParams = { ...params };
        delete queryParams['clientId'];
        delete queryParams['addressId'];
        await this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: queryParams,
          replaceUrl: true, // same page: no extra history entry for Back/Volver to land on
        });
      }
    } catch (error) {
      console.error('Error al obtener el id del cliente');
    }
  }

  disabledGoStepAddress() {
    return this.stepClientComponent.validClientForm();
  }

  getCrmParameters() {
    let opportunityId: any = undefined;

    this.activatedRoute.queryParams.subscribe((params) => {
      opportunityId = params['opportunityId'];
      this.fromCMR = !!opportunityId;
    });

    if (opportunityId) {
      this.workTaskSrv
        .getCrmTaskReport(Number.parseInt(opportunityId))
        .subscribe({
          next: (data) => {
            if (data) {
              this.workloadId = data.workloadId;
              this.getClient(data.clientId);
            } else {
              this.SwalFireMessage(
                'CRM',
                'La orden de trabajo no fue encontrada. Por favor revise los datos del cliente.'
              ).then((result) => {
                this.router.navigate(['/workload']);
              });
            }
          },
          error: (error) => {
            this.toastr.error(
              'Error',
              'Se produjo un error al buscar la orden'
            );
          },
        });
    }
  }

  async getClient(clientId: any) {
    try {
      if (clientId) {
        const data = await firstValueFrom(
          this.clientService.getClientProfile(clientId)
        );
        this.stepClientComponent.clientForm.patchValue(data);
        this.stepClientComponent.changeSelectedType(
          data.identificationTypeId,
          false
        );
        this.stepClientComponent.wasSearched = true;
        this.goStepAddress(this.stepper);
      }
    } catch (error) {
      console.error('Error getting client data', error);
    }
  }

  goStepAddress(stepper: MatStepper) {
    this.stepClientComponent.noMailClient();
    if (this.stepClientComponent.validClientForm()) {
      let clientId = this.stepClientComponent.getClientId();
      this.clientService
        .getClientProfile(clientId)
        .subscribe((data: ClientModel) => {
          if (clientId != Guid.EMPTY) {
            this.stepAddressComponent.getAddresses(clientId, this.addressIdToSelect);
            this.addressIdToSelect = undefined; // only for the first time the step opens
          } else {
            this.stepAddressComponent.cleanStep();
          }
          this.workloadForm.controls['client'].setValue(
            this.stepClientComponent.clientForm.getRawValue()
          );
          stepper.next();
        });
    }
  }

  goStepWorkTask(stepper: MatStepper) {
    if (
      this.stepAddressComponent.isValidAddress(AddressTypeEnum.LegalAddress) ||
      this.stepAddressComponent.isDisabledAddressForm(
        AddressTypeEnum.LegalAddress
      )
    ) {
      if (!this.stepAddressComponent.coordinates?.lat) {
        this.toastr.error('Error', 'Debe ubicar el domicilio en el mapa.');
        return;
      } else {
        this.coordinates = {
          latitude: this.stepAddressComponent.coordinates?.lat,
          longitude: this.stepAddressComponent.coordinates?.long,
        };

        stepper.next();
      }
    } else {
      this.stepAddressComponent.markAllAsTouched(AddressTypeEnum.LegalAddress);
    }
  }

  submitWorkload(stepper: MatStepper) {
    this.disableSubmit = true;

    if (
      this.stepWorkTaskComponent != undefined &&
      this.stepWorkTaskComponent.jobsList.length > 0
    ) {
      let address = this.stepAddressComponent.getAddressValue(
        AddressTypeEnum.LegalAddress
      );

      let workTasks = this.stepWorkTaskComponent.jobsList;

      if (!this.workloadId || this.workloadId == Guid.EMPTY) {
        let client = this.workloadForm.controls['client'].value as ClientModel;

        client.phone.area = client.phone?.area?.toString();
        client.phone.number = client.phone?.number?.toString();

        const work: WorkloadModel = {
          client: client,
          address: address,
          workTasks: workTasks,
        };
        console.info(work);
        this.workloadSvc.createWorkload(work).subscribe({
          next: (data: any) => {
            this.toastr.success(
              'Registrada',
              'La Orden de Trabajo se ha registrado correctamente.'
            );
            this.router.navigate(['/scheduleTasks'], { fragment: 'anchor' });
          },
          error: (err) => this.handleSubmitError(err, stepper),
        });
      } else {
        const work: WorkloadModel = { address: address, workTasks: workTasks };
        console.info(work);
        console.warn(this.workloadId);

        this.workloadSvc.completeWorkloadInfo(this.workloadId, work).subscribe({
          next: () => {
            this.toastr.success(
              'Registrada',
              'La Orden de Trabajo se ha registrado correctamente.'
            );
            this.router.navigate(['/scheduleTasks']);
          },
          error: (err) => this.handleSubmitError(err, stepper),
        });
      }
    } else {
      this.toastr.error(
        'Error',
        'Debe agregar al menos un trabajo a realizar.'
      );
      this.checkDisabledFinish();
    }
  }

  // The wizard keeps everything the user entered: show what the API rejected and let them fix it and retry.
  handleSubmitError(err: unknown, stepper?: MatStepper) {
    const { messages, fieldErrors } = parseApiValidationError(err);
    const message = messages.length
      ? messages.join(' · ')
      : 'Revise los datos ingresados e intente nuevamente.';
    // Kept on screen until closed: the user needs it while fixing the data.
    this.toastr.error('No se pudo registrar la Orden de Trabajo', message, {
      disableTimeOut: true,
    });

    let clientFieldFailed = false;
    for (const { path, message: fieldMessage } of fieldErrors) {
      if (!path.startsWith('client.')) continue;
      const control = this.stepClientComponent?.clientForm.get(path.substring('client.'.length));
      if (control) {
        control.setErrors({ ...control.errors, api: fieldMessage });
        control.markAsTouched();
        clientFieldFailed = true;
      }
    }
    if (clientFieldFailed && stepper) {
      stepper.selectedIndex = 0;
    }

    this.checkDisabledFinish();
  }

  checkDisabledFinish() {
    this.disableSubmit =
      !this.stepWorkTaskComponent ||
      this.stepWorkTaskComponent.jobsList.length == 0;
    return this.disableSubmit;
  }

  SwalFireMessage(title: string, text: string): Promise<SweetAlertResult> {
    return Swal.fire({
      title: title,
      html: `<div class="swal2-content-custom">${text}</div>`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCancelButton: false,
      confirmButtonText: 'Ok',
      confirmButtonColor: '#E61414',
      customClass: {
        title: 'swal2-title-custom',
        popup: 'swal2-popup-custom',
        confirmButton: 'swal2-confirm-custom',
        actions: 'swal2-actions-custom',
      },
    });
  }
}
